/*
 *
 * Accounts reducer
 *
 */
import Immutable from "immutable"
import actionTypes, * as actionGroups from "app/static/frontend/accounts/account-action-types"
import { agreeToTextingActionDict } from "texting/action-types"

import { getPersonaOptions } from "shared/imports/sharedHelperFunctions"
import { ORG_DEFAULT_EMAIL_LIMIT } from "shared/imports/sharedConstants"

export const noPermissions = () => ({
    qp_federal: false,
    qp_eu: false,

    qp_grassroots: [],
    qp_states: [],
    qp_local: [],
    qp_school_boards: [],
    qp_stakeholder: false,
    qp_international: [],

    qp_legislators: [],
    qp_executives: [],
    qp_staffers: [],
    qp_legislative_data: [],
    qp_legislative_profiles: [],
    qp_official_documents: [],
    qp_regulations: [],
    qp_media_monitoring: [],
    qp_media_relations: [],

    qp_custom_data: false,
    qp_external_relationship: false,
    qp_external_interactions: false,
    qp_internal_relationship: false,
    qp_action_center: false,
    qp_officials_outbox: false,
    qp_legislation_page: false,
    qp_custom_contacts: false,
    qp_custom_outbox: false,
    qp_custom_events: false,
    qp_contacts_documents: false,
    qp_project_management: false,
    qp_unique_ids_in_download: false,
    qp_consultants: false,
    qp_live_chat: false,
    qp_qpac_v1: false,
})

export const createEmptyOrg = () => {
    const org = {
        account_type: DjangIO.app.userdata.types.AccountType.normal.value,
        id: undefined,
        lower_tier_bound: 3,
        max_csv_size: 1000,
        email_limit: ORG_DEFAULT_EMAIL_LIMIT,
        max_email_alerts: 1000,
        max_members: 1,
        max_projects: 100,
        name: "",
        partners: [],
        trial_expiration: "",
        upper_tier_bound: 6,
        internal_interaction_types: DjangIO.app.userdata.types.InteractionType.region_supported_items()
            .filter((item) => item.loggable)
            .filter(
                (item) => item.order !== null, // only types with an order are defaults
            )
            .sort((a, b) => a.order - b.order)
            .map((type) => type.value),
        internal_relationship_types: DjangIO.app.userdata.types.RelationshipType.items()
            .sort((a, b) => a.order - b.order)
            .map((type) => type.value),
    }

    // add empty permissions to other org arguments
    return Object.assign(org, noPermissions())
}

export const createEmptyTeam = () => ({
    members: [],
    name: "",
})

export const createEmptyUser = () => ({
    bulk_action_max_size: 100,
    can_access_all_org_regions: true,
    can_access_bulk_uploads: true,
    can_access_custom_fields_management: true,
    can_access_grassroots: true,
    can_access_issue_management: true,
    can_access_campaign_finance: true,
    can_access_media_monitoring: false,
    can_access_media_relations: false,
    can_access_notes: true,
    can_access_outbox: true,
    can_access_pac: false,
    can_change_action_center_settings: true,
    can_see_any_previous_outbox_email: true,
    can_see_supporters_and_custom_orgs: true,
    can_send_from_any_outbox: true,
    can_access_axios_articles: false,
    default_limited_to_teams: [],
    // default_saved_searches for default Persona are created in UserForm componendDidMount
    // default_search_data_type will be correctly set by persona_type.default_search_data_type in create_or_update_user_info
    email: "",
    first_name: "",
    last_name: "",
    other_personas: [DjangIO.app.userdata.models.Persona.default.value],
    password_first: "",
    password_score: 0,
    password_second: "",
    persona: DjangIO.app.userdata.models.Persona.default.value,
    projects: [],
    search_data_type_order: DjangIO.app.userdata.models.Persona.default.search_data_type_order,
    sidebar_settings_list: DjangIO.app.userdata.models.Persona.default.sidebar_settings_list,
    teams: [],
    user_regions: [],
    username: "",
    single_login: false,
})

export const createEmptyAdminUser = () => ({
    email: "",
    first_name: "",
    last_name: "",
    username: "",
    persona: undefined,
    other_personas: [],
    can_access_all_org_regions: true,
    can_access_custom_fields_management: true,
    can_access_grassroots: true,
    can_access_bulk_uploads: true,
    can_access_issue_management: true,
    can_access_campaign_finance: true,
    can_access_media_monitoring: true,
    can_access_media_relations: true,
    can_access_notes: true,
    can_access_outbox: true,
    can_access_pac: false,
    can_access_texting: true,
    can_change_action_center_settings: true,
    can_see_any_previous_outbox_email: true,
    can_see_supporters_and_custom_orgs: true,
    can_send_from_any_outbox: true,
    can_access_axios_articles: false,
    password_first: "",
    password_second: "",
    password_score: 0,
    single_login: false,
})

const initialState = Immutable.fromJS({
    AccountInfo: {
        user: createEmptyUser(),
        userUiState: {
            editingUser: false,
            loadingUser: true,
            isSearchifying: false,
            isResourceTableShowingActiveUsers: true,
            loadingTeamsForUserForm: false,
            isUserFormWorking: false,
            isTwoFactorFormWorking: false,
            sendActivationEmail: true,
            defaultProject: {
                id: undefined,
            },
            defaultTeam: {
                id: undefined,
            },
        },
        // If creating a user on behalf of another organization, the teams are not the same as the
        // current signed in user's teams. Store the teams for the current organization being edited here
        teams: [],
    },
    OrgDesign: {},
    TeamManagement: {
        team: createEmptyTeam(),
        teamUiState: {
            editingTeam: true,
            loadingTeam: true,
        },
    },
    OrgManagement: {
        users: {},
        orgs: {},
        OrgPermissions: {
            states: [],
            local: [],
            grassroots: [],
            international: [],
        },
        OrgInfo: {},
        organization: createEmptyOrg(),
        organizationUiState: {
            editingOrg: true,
            loadingOrg: true,
            newDomain: "",
            shouldNewDomainRoute: false,
            showAdvanced: false,
            isResourceTableShowingActiveOrgs: true,
            isOrgFormWorking: false,
            orgUserCount: 0,
            editingProduct: false,
        },
    },
})

export const getInitialState = (kwargs = {}) => initialState.merge(kwargs)

export const accountReducer = (state = initialState, action) => {
    switch (action.type) {
        case actionTypes.GET_ORG_SUCCESS:
            return state
                .setIn(["OrgManagement", "organization"], Immutable.fromJS(action.response))
                .setIn(["OrgManagement", "organizationUiState", "loadingOrg"], false)
        case actionTypes.EDIT_ORG:
            return state.setIn(["OrgManagement", "organization", action.key], Immutable.fromJS(action.value))
        case actionTypes.EDIT_ORG_SET_IN:
            return state.setIn(["OrgManagement", "organization", ...action.path], Immutable.fromJS(action.value))
        case actionTypes.EDIT_ORG_UI_STATE:
            return state.setIn(["OrgManagement", "organizationUiState", action.key], Immutable.fromJS(action.value))
        case actionTypes.EDIT_PRODUCTS:
            return state.updateIn(["OrgManagement", "organization"], (info) => info.merge(action.productObj))
        case actionTypes.CHANGE_INFO:
            // if we are editing the teams this User is on, make sure that we clear out
            // default limited_to_teams that cannot apply
            if (action.key === "teams") {
                const filteredDefaultLimitedToTeams = (
                    state.getIn(["AccountInfo", "user", "default_limited_to_teams"]) || []
                ).filter((teamUri) => action.value.includes(teamUri))
                return state
                    .setIn(["AccountInfo", "user", action.key], Immutable.fromJS(action.value))
                    .setIn(["AccountInfo", "user", "default_limited_to_teams"], filteredDefaultLimitedToTeams || [])
            } else {
                return state.setIn(["AccountInfo", "user", action.key], Immutable.fromJS(action.value))
            }
        // when we create a blank user, we want to set the all applicable user types to all of the available personas
        // that the organization has permission for
        case actionTypes.SET_DEFAULT_ALL_APPLICABLE_USER_TYPES:
            return state.setIn(
                ["AccountInfo", "user", "other_personas"],
                Immutable.fromJS(getPersonaOptions(state.getIn(["OrgManagement", "organization"]))),
            )

        // if the org has only 1 product, set the primary product to be that product. Otherwise, do nothing
        // as the value already is Default
        case actionTypes.SET_PRIMARY_PRODUCT_USED:
            if (state.getIn(["AccountInfo", "user", "other_personas"]).size === 2) {
                // find the non-default persona value
                const primaryProductValue = state
                    .getIn(["AccountInfo", "user", "other_personas"])
                    .find((persona) => !persona.get("default"))
                    .get("value")
                return state.setIn(["AccountInfo", "user", "persona"], primaryProductValue)
            } else {
                return state
            }

        // set up the initial state for the user form
        case actionTypes.INIT_USER_FORM:
            return state
                .setIn(["AccountInfo", "user"], Immutable.fromJS(createEmptyUser()))
                .setIn(["AccountInfo", "userUiState", "loadingUser"], false)

        case actionTypes.GET_USER_START:
            return state.setIn(["AccountInfo", "userUiState", "loadingUser"], true)

        case actionTypes.GET_USER_SUCCESS:
            return state
                .setIn(["AccountInfo", "user"], Immutable.fromJS(action.response))
                .setIn(["AccountInfo", "userUiState", "loadingUser"], false)
                .setIn(["AccountInfo", "userUiState", "editingUser"], true)
        case actionTypes.GET_USERS_SUCCESS:
            return state.setIn(["OrgManagement", "users"], Immutable.fromJS(action.response))
        case actionTypes.EDIT_USER_UI_STATE:
            return state.setIn(["AccountInfo", "userUiState", action.key], Immutable.fromJS(action.value))

        case actionTypes.GET_TEAM_SUCCESS:
            return state
                .setIn(["TeamManagement", "team"], Immutable.fromJS(action.response))
                .setIn(["TeamManagement", "teamUiState", "loadingTeam"], false)

        case actionGroups.getTeamsForUserFormTypes.start:
            return state.setIn(["AccountInfo", "userUiState", "loadingTeamsForUserForm"], true)
        case actionGroups.getTeamsForUserFormTypes.success:
            return state
                .setIn(["AccountInfo", "teams"], Immutable.fromJS(action.response.objects))
                .setIn(["AccountInfo", "userUiState", "loadingTeamsForUserForm"], false)
        case actionGroups.getTeamsForUserFormTypes.fail:
            return state.setIn(["AccountInfo", "userUiState", "loadingTeamsForUserForm"], false)

        case actionTypes.EDIT_TEAM:
            return state.setIn(["TeamManagement", "team", action.key], Immutable.fromJS(action.value))
        case actionTypes.EDIT_TEAM_UI_STATE:
            return state.setIn(["TeamManagement", "teamUiState", action.key], Immutable.fromJS(action.value))
        case actionTypes.EDIT_FILTERS:
            return state.setIn(action.keyPath, action.value)
        case actionTypes.USER_FORM_START_SEARCHIFY:
            return state.setIn(["AccountInfo", "userUiState", "isSearchifying"], true)

        case actionTypes.DEACTIVATE_USER:
            return state.update("OrgManagement", (org) => org.update("users", (users) => users.set(action.user, true)))
        case actionTypes.REACTIVATE_USER:
            return state.update("OrgManagement", (org) => org.update("users", (users) => users.set(action.user, false)))
        case actionTypes.TOGGLE_PICKER:
            return state.set(action.picker, !state.get(action.picker))
        case actionTypes.CHANGE_DESIGN:
            return state.merge(action.OrgDesign)
        case actionGroups.getDefaultDesign.success:
            return state.merge(action.response.objects[0])
        case actionTypes.ADD_PRODUCT:
            return state.updateIn(["OrgManagement", "OrgPermissions"], (info) => info.merge(action.productObj))
        case actionTypes.GET_USER_COUNT:
            return state.update("OrgManagement", (org) => org.set("user_count", action.limit))
        case actionTypes.SET_VALID:
            return state.set("valid", action.isValid)
        case actionTypes.TOGGLE_CREATE_MODE:
            return state
                .set("isCreatingNewDesign", !state.get("isCreatingNewDesign"))
                .set("default", false)
                .set("id", undefined)
                .set("design_name", undefined)
                .set("limited_to_teams", Immutable.fromJS(Userdata.default_limited_to_teams))
        case actionTypes.RESET_FORM:
            return state.set("isCreatingNewDesign", false)
        case actionTypes.DELETE_DESIGN:
            return state.set("archived", true)

        // add this case here to store whether a user has accepted the terms & conditions
        // for texting for the entire org so that the values can be shared across both the campaign
        // and the action center forms
        case agreeToTextingActionDict.success:
            return state.mergeIn(["OrgManagement", "organization"], action.response)

        case actionGroups.verifyDomainActionDict.success:
            const pathToDomains = ["OrgManagement", "organization", "_extra", "email_domains"]
            // get the index of the domain that we have just verified so we can update it
            const domainToUpdateIndex = state
                .getIn(pathToDomains)
                .findIndex((elem) => elem.get("id") === action.response.id)
            return state.setIn([...pathToDomains, domainToUpdateIndex], Immutable.fromJS(action.response))

        default:
            return state
    }
}
